import { API_CONFIG } from '@/config/constants';
import { getToken, clearAuthData } from './auth';
import { IMPERSONATION_HEADER, getImpersonation, stopImpersonation } from './impersonation';

/** Map known backend messages to user-friendly Spanish */
const ERROR_MESSAGES: Record<string, string> = {
  'Mapping With This Document side, Document type and Nationality Is Already Exist': 'Esta regla de mapeo ya existe',
  'Both rows are managed automatically and cannot be created directly.': 'Las filas «Ambas caras» se gestionan automáticamente y no pueden crearse manualmente.',
  'Both rows are managed automatically and cannot be edited directly.': 'Las filas «Ambas caras» se gestionan automáticamente y no pueden editarse manualmente.',
  'Both rows are managed automatically and cannot be deleted directly.': 'Las filas «Ambas caras» se gestionan automáticamente y no pueden eliminarse manualmente.',
  'Preview must be requested with the Front or Back hypothesis, not Both.': 'La vista previa solo puede solicitarse desde Anverso o Reverso.',
};

const translateError = (msg: string): string => ERROR_MESSAGES[msg] || msg;

export class HttpError extends Error {
  status: number;
  data: unknown;
  constructor(message: string, status: number, data?: unknown) {
    super(message);
    this.name = 'HttpError';
    this.status = status;
    this.data = data;
  }
}

interface RequestOptions extends Omit<RequestInit, 'body'> {
  body?: unknown;
  skipAuth?: boolean;
}

interface HttpResponse<T> {
  data: T;
  status: number;
  ok: boolean;
}

const request = async <T>(
  endpoint: string,
  options: RequestOptions = {}
): Promise<HttpResponse<T>> => {
  const { body, skipAuth = false, headers: customHeaders, ...restOptions } = options;

  const isFormData = body instanceof FormData;

  const headers: Record<string, string> = {
    Accept: '*/*',
    // Don't set Content-Type for FormData — browser sets it with boundary
    ...(!isFormData && { 'Content-Type': 'application/json' }),
    ...(customHeaders as Record<string, string>),
  };

  if (!skipAuth) {
    const token = getToken();
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }
  }

  // Downward impersonation: the actor keeps their own token; the API swaps the
  // request's principal for this subject after validating the real role.
  const impersonation = skipAuth ? null : getImpersonation();
  if (impersonation) {
    headers[IMPERSONATION_HEADER] = impersonation.sub;
  }

  let requestBody: BodyInit | undefined;
  if (body) {
    requestBody = isFormData ? body as BodyInit : JSON.stringify(body);
  }

  const config: RequestInit = {
    ...restOptions,
    headers,
    body: requestBody,
  };

  const url = endpoint.startsWith('http')
    ? endpoint
    : `${API_CONFIG.BASE_URL}${endpoint}`;

  const response = await fetch(url, config);

  if (response.status === 401 && !skipAuth) {
    clearAuthData();
    const error = new HttpError('Unauthorized', 401, { authError: true });
    throw error;
  }

  // Handle 204 No Content
  if (response.status === 204) {
    return { data: {} as T, status: 204, ok: true };
  }

  let data: T;
  const contentType = response.headers.get('content-type');
  if (contentType?.includes('application/json')) {
    data = await response.json();
  } else {
    data = (await response.text()) as unknown as T;
  }

  if (!response.ok) {
    // Failsafe: if the API rejects the impersonation itself (target revoked,
    // disabled, role raised…), drop the state and come back as the real user
    // instead of leaving every request failing.
    if (
      impersonation &&
      response.status === 403 &&
      String((data as { code?: string })?.code ?? '').startsWith('IMPERSONATION')
    ) {
      stopImpersonation();
      window.location.hash = '#/';
      window.location.reload();
    }

    const rawMessage = typeof data === 'string'
      ? data
      : (data as { message?: string })?.message || response.statusText;
    const error = new HttpError(
      translateError(rawMessage),
      response.status,
      data
    );
    throw error;
  }

  return { data, status: response.status, ok: response.ok };
};

export const httpClient = {
  get: <T>(endpoint: string, options?: RequestOptions) =>
    request<T>(endpoint, { ...options, method: 'GET' }),
  post: <T>(endpoint: string, body?: unknown, options?: RequestOptions) =>
    request<T>(endpoint, { ...options, method: 'POST', body }),
  put: <T>(endpoint: string, body?: unknown, options?: RequestOptions) =>
    request<T>(endpoint, { ...options, method: 'PUT', body }),
  delete: <T>(endpoint: string, options?: RequestOptions) =>
    request<T>(endpoint, { ...options, method: 'DELETE' }),
};

export default httpClient;
